import React, { useMemo } from 'react';
import propTypes from 'prop-types';
import dayjs from 'dayjs';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Button from 'tailwind-components/Button/Button';
import { cn } from 'lib/utils';

/**
 * Renders one "Commission at risk" recommendation card.
 *
 * Item shape (AgencyNotAppointedPolicy):
 *   { policyId, agentId, agentName, carrierId, carrierName, state, createdAt }
 */

function getFirstName(fullName) {
  const trimmed = String(fullName || '').trim();
  if (!trimmed) return '';
  return trimmed.split(/\s+/)[0];
}

function formatPolicyDate(value, fallback = '') {
  if (!value) return fallback;
  const d = dayjs(value);
  if (!d.isValid()) return fallback;
  return d.format('MM/DD/YYYY');
}

function formatShortDate(value) {
  if (!value) return '';
  const d = dayjs(value);
  if (!d.isValid()) return '';
  return d.format('MM/DD');
}

function CommissionAtRiskCard({
  policy,
  className,
  onNext,
  onPrev,
  onSuggest,
  suggestLoading = false,
  position,
  total
}) {
  const hasMultiple = Number.isFinite(Number(total)) && Number(total) > 1;
  const showCounter = Number.isFinite(Number(position)) && hasMultiple;
  const carrierName = policy?.carrierName || 'this carrier';
  const agentName = policy?.agentName || 'Unknown agent';
  const state = policy?.state || '';
  const firstName = getFirstName(agentName);
  const policyShortDate = formatShortDate(policy?.createdAt);
  const policyFullDate = formatPolicyDate(policy?.createdAt);

  const description = useMemo(() => {
    const subject = firstName || 'The agent';
    const dateClause = policyShortDate ? ` on ${policyShortDate}` : '';
    const stateClause = state ? ` in ${state}` : '';
    return (
      `${subject} sold a policy with ${carrierName}${dateClause} without an ` +
      'active appointment. An appointment is required to ensure commissions ' +
      `are paid for this and future ${carrierName} sales${stateClause}.`
    );
  }, [firstName, carrierName, policyShortDate, state]);

  const subtitleParts = [agentName, state].filter(Boolean);

  return (
    <article
      className={cn(
        'flex w-full min-w-0 flex-col gap-3 overflow-hidden rounded-[10px]',
        'border border-grey-lighter bg-white p-4 shadow-[0_2px_6px_rgba(0,0,0,0.06)]',
        className
      )}
    >
      <div className='flex items-center justify-between gap-2'>
        <span
          className={cn(
            'inline-flex w-fit items-center rounded-full px-2.5 py-1',
            'text-[10px] font-bold tracking-[0.08em] uppercase',
            'bg-primary-light text-primary-darkened'
          )}
        >
          Commission at risk
        </span>
        <Button
          variant='primary'
          size='xsmall'
          rounded='small'
          text='Suggest to agent'
          onClick={onSuggest}
          isLoading={suggestLoading}
          isDisabled={!onSuggest || suggestLoading}
          className='shrink-0'
        />
      </div>

      <div className='flex min-w-0 flex-col gap-0.5'>
        <h4 className='m-0 text-base leading-snug font-semibold text-primary-darkened'>
          {carrierName}
        </h4>
        {subtitleParts.length > 0 && (
          <p className='m-0 text-sm font-medium text-primary-emphasis-light'>
            {subtitleParts.join(' · ')}
          </p>
        )}
        {policy?.agencyName && (
          <p className='m-0 text-xs text-grey'>{policy.agencyName}</p>
        )}
      </div>

      <p className='m-0 text-sm leading-snug text-grey-dark'>{description}</p>

      {policyFullDate && (
        <span className='text-xs text-grey'>
          <span className='font-semibold text-grey-dark'>Policy sold:</span>{' '}
          {policyFullDate}
        </span>
      )}

      {hasMultiple && (
        <div className='flex items-center justify-between gap-2 border-t border-grey-lighter pt-3'>
          <Button
            variant='default'
            outlined
            size='xsmall'
            rounded='small'
            icon={<ChevronLeft />}
            text='Previous'
            onClick={onPrev}
            isDisabled={!onPrev}
          />
          {showCounter && (
            <span className='text-[11px] font-medium text-grey'>
              {position} of {total}
            </span>
          )}
          <Button
            variant='default'
            outlined
            size='xsmall'
            rounded='small'
            text='Next'
            icon={<ChevronRight />}
            iconPosition='right'
            onClick={onNext}
            isDisabled={!onNext}
          />
        </div>
      )}
    </article>
  );
}

CommissionAtRiskCard.propTypes = {
  policy: propTypes.shape({
    policyId: propTypes.string,
    agentId: propTypes.string,
    agentName: propTypes.string,
    carrierId: propTypes.string,
    carrierName: propTypes.string,
    state: propTypes.string,
    createdAt: propTypes.string,
    agencyName: propTypes.string
  }).isRequired,
  className: propTypes.string,
  onNext: propTypes.func,
  onPrev: propTypes.func,
  onSuggest: propTypes.func,
  suggestLoading: propTypes.bool,
  position: propTypes.number,
  total: propTypes.number
};

export default CommissionAtRiskCard;
